<script setup lang="ts">
import type { TabsItem } from '@nuxt/ui'
import { computed } from 'vue'

const props = defineProps<{
  product: SingleProduct
}>()

const subjectTagSet = computed(() => {
  const tags = props.product.variants.map(({ subjects, series }) => {
    // https://acoundervisning.atlassian.net/browse/EHU-898?focusedCommentId=117425
    if (series && series?.toLowerCase() === 'god i norsk') {
      return 'FOV og Norsk andrespråk'
    }

    if (subjects) {
      if (subjects.length === 0 || subjects.length >= 8) {
        return 'Alle fag'
      }

      if (subjects.length <= 2) {
        return subjects
          .map((s) => {
            const labels = SUBJECT_LABELS[s]
            return Array.isArray(labels) ? labels[1] : labels
          })
          .join('/')
      }

      // https://acoundervisning.atlassian.net/browse/EHU-898?focusedCommentId=117425
      if (subjects.some((s) => ['SAK', 'SAF', 'SOK', 'SOS', 'POL'].includes(s))) {
        return 'Samfunns- og sosiologifag'
      }
    }

    return 'Tverrfaglig'
  })

  return new Set(tags)
})
const tags = computed(() => [...subjectTagSet.value, ...(props.product.levels || [])])
const tabs: ComputedRef<TabsItem[]> = computed(() => [
  {
    label: `Om ${props.product.name}-bøkene`,
    content: props.product.aboutBooks,
    image: 'bøker_placeholder.png',
  },
  {
    label: `Om digitalt til ${props.product.name}`,
    labelSmall: 'Om digitalt',
    content: props.product.aboutDigitalResources,
    image: 'digitalressurs_placeholder.png',
  },
].filter(({ content }) => content))
</script>

<template>
  <UContainer
    class="grid gap-y-16 pb-16 lg:items-center lg:pt-20 lg:pb-0"
    :class="product.cover?.url ? 'lg:grid-cols-2' : 'lg:grid-cols-1'"
  >
    <CrystallizeImage
      :image="product.cover"
      img-class="max-w-full object-contain lg:pl-20"
      class="relative max-w-sm place-self-center lg:max-w-none"
    />
    <div class="-mt-30 flex flex-col gap-8 rounded-br-(--roundness) bg-(--theme-5) px-6 py-12 text-(--theme-60) sm:px-12 lg:col-start-1 lg:row-start-1 lg:mt-0 lg:p-20">
      <h1
        class="text-center font-headline text-5xl font-medium lg:-mt-4 lg:text-start"
        v-text="product.title"
      />
      <div class="flex flex-wrap gap-2 text-xs">
        <span
          v-for="tag in tags.filter(x => x)"
          :key="tag"
          class="flex-none rounded-full bg-(--theme-50) px-5 py-3 text-(--theme-5) uppercase"
          v-text="tag"
        />
      </div>
      <div
        class="text-center text-lg lg:text-start"
        v-html="product.intro"
      />
      <div
        class="max-w-2xl self-center text-base [&_ul]:flex [&_ul]:list-inside [&_ul]:list-disc [&_ul]:flex-col [&_ul]:gap-1 lg:[&_ul]:list-outside lg:[&_ul]:gap-3"
        v-html="product.description"
      />
      <a
        class="flex items-center gap-6 self-center rounded-full bg-yellow-40 px-11 py-xs text-xl font-semibold text-gray-60 no-underline transition-colors hover:bg-yellow-30"
        href="#product-variants"
      >
        Gå til produkt&shy;listen
        <UIcon name="far:arrow-down" />
      </a>
    </div>

    <article v-if="product.news?.title" class="relative rounded-tr-[calc(var(--roundness)+8px)] bg-[repeating-linear-gradient(-45deg,var(--color-yellow-30)_0px,var(--color-yellow-30)_1.5px,transparent_1.5px,transparent_6px)] text-(--theme-50) md:p-2 lg:col-span-2">
      <SvgNewsCircle class="absolute top-0 -right-20 hidden rotate-on-scroll fill-(--theme-pop) lg:block" />
      <div class="relative flex gap-6 rounded-tr-(--roundness) bg-yellow-30 p-6">
        <CrystallizeImage
          :image="product.news.image"
          class=" hidden w-2/5 flex-none md:block md:w-64"
          img-class="rounded-sm md:rounded-bl-(--roundness) md:rounded-tr-(--roundness) rounded-tl-(--roundness) md:rounded-tl-sm"
        />
        <div class="mx-2 space-y-2">
          <strong class="inline-block text-sm font-bold uppercase">Aktuelt</strong>
          <h2 class="text-lg font-bold [word-break:break-word] md:text-2xl">
            {{ product.news.title }}
          </h2>
          <div
            v-if="product.news.text"
            class="article-body space-y-2"
            v-html="product.news.text"
          />
          <a
            v-if="product.news.article"
            :href="product.news.article.slug.full"
            class="mt-2 inline-block rounded-full border px-4 py-2 text-sm no-underline"
          >Les mer</a>
        </div>
      </div>
    </article>

    <div v-if="tabs.length <= 0" class="lg:mb-16" />

    <UTabs
      v-else
      :items="tabs"
      :ui="{
        root: 'gap-0 w-full lg:col-span-2',
        list: ['w-full px-0 max-w-(--ui-container) mx-auto bg-inherit py-0 grid grid-flow-col auto-cols-fr items-end [--roundness:23px] min-h-20', tabs.length < 2 ? 'hidden' : ''],
        content: ['bg-(--theme-5) relative text-(--theme-60) w-full rounded-r-(--roundness)', tabs.length > 1 ? 'lg:rounded-r-none' : ''],
        trigger: 'bg-(--theme-20) shadow-none border-b-[200px] mb-[-200px] border-b-(--theme-20) data-active:border-b-(--theme-5) hover:border-b-(--theme-10) hover:bg-(--theme-10) !text-(--theme-60) data-active:!text-(--theme-50) rounded-b-none rounded-t-(--roundness) p-4 text-xl data-active:bg-(--theme-5) data-active:underline transition-all font-normal data-active:font-semibold data-active:py-6 h-fit underline-offset-4',
        indicator: 'rounded-b-none rounded-t-(--roundness) inset-y-0',
        label: 'text-inherit',
      }"
    >
      <template #default="{ item }">
        <span class="hidden md:inline">{{ item.label }}</span>
        <span class="md:hidden">{{ item.labelSmall ?? item.label }}</span>
      </template>

      <template #content="{ item }">
        <div class="relative grid grid-rows-[max-content_1fr] gap-x-20 gap-y-8 p-6 [grid-template-areas:'body'_'.'_'usp'] lg:grid-cols-2 lg:p-20 lg:text-xl lg:[grid-template-areas:'title_img'_'body_img'_'._.'_'usp_usp']">
          <h2 :class="{ 'sr-only': tabs.length > 1 }">
            {{ item.label }}
          </h2>

          <div class="space-y-8 text-base [grid-area:body]" v-html="item.content" />

          <StaticAsset
            v-if="item.image"
            folder="images"
            :file="item.image"
            class="hidden [grid-area:img] lg:block"
          />

          <ul v-if="product.usp && product.usp.length > 1" class="grid auto-cols-fr gap-8 text-base [grid-area:usp] lg:grid-flow-col">
            <li
              v-for="{ text } in product.usp.slice(0, 3)"
              :key="text"
              class="grid place-content-center bg-(--theme-10) p-10 text-center text-(--theme-50) not-first:rounded-br-(--roundness) first:rounded-bl-(--roundness) lg:not-first:rounded-br-none lg:not-last:rounded-bl-(--roundness) lg:last:rounded-tr-(--roundness)"
              v-html="text"
            />
          </ul>

          <hr v-else class="border-(--theme-10) [grid-area:usp]" />

          <SvgAboutProductCircle class="absolute bottom-20 -left-20 hidden rotate-on-scroll fill-(--theme-pop) lg:block" />
        </div>
      </template>
    </UTabs>
  </UContainer>
</template>
